import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userAPI, customerAPI } from '../services/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Trash2, IndianRupee, Edit2 } from 'lucide-react';
import { format } from 'date-fns';
import RightCheckbox from '../components/common/RightCheckbox';
import useDarkReady from '../theme/useDarkReady';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

function StatCard({ label, total, customers, color, bg, border }) {
  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '18px 20px', flex: 1, minWidth: 160 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <IndianRupee size={13} color={color} />
        <span style={{ fontSize: '.73rem', color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: 900, color }}>{fmt(total)}</div>
      <div style={{ fontSize: '.73rem', color: 'var(--text-muted)', marginTop: 4 }}>From {customers} Customer{customers !== 1 ? 's' : ''}</div>
    </div>
  );
}

export default function AgentDetailPage() {
  useDarkReady();
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData]         = useState(null);
  const [areas, setAreas]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selAreas, setSelAreas] = useState([]);
  const [areaSearch, setAreaSearch] = useState('');
  const [fieldModules, setFieldModules] = useState([]);
  const [fieldRights, setFieldRights]   = useState({});
  const [savingRights, setSavingRights] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', mobile: '', password: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [month, setMonth] = useState(format(new Date(), 'yyyy-MM'));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [det, areasRes, registryRes] = await Promise.all([
        userAPI.getAgentDetail(id, month),
        customerAPI.getAreas({ customerType: 'catv,both' }),
        userAPI.getModuleRegistry(),
      ]);
      const agent = det.data.data.agent;
      const modules = (registryRes.data.data || []).filter(m => m.section === 'FIELD AGENT APP');
      setData(det.data.data);
      setSelAreas(agent.assignedAreas || []);
      setAreas((areasRes.data.data || []).filter(Boolean).sort());
      setFieldModules(modules);
      setFieldRights(Object.fromEntries(modules.map(m => [
        m.key,
        Object.fromEntries(m.actions.map(a => [a.key, !!agent.fieldRights?.[m.key]?.[a.key]])),
      ])));
    } catch {
      toast.error('Failed to load agent detail');
    } finally {
      setLoading(false);
    }
  }, [id, month]);

  useEffect(() => { load(); }, [load]);

  const handleSaveAreas = async () => {
    setSaving(true);
    try {
      await userAPI.updateAreas(id, selAreas);
      toast.success('Agent areas updated');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to update areas'); }
    finally { setSaving(false); }
  };

  const toggleFieldRight = (moduleKey, action) => {
    setFieldRights(r => ({ ...r, [moduleKey]: { ...r[moduleKey], [action]: !r[moduleKey][action] } }));
  };

  const handleSaveFieldRights = async () => {
    setSavingRights(true);
    try {
      await userAPI.updateFieldRights(id, fieldRights);
      toast.success('Field Collection Rights updated');
    } catch { toast.error('Failed to update Field Collection Rights'); }
    finally { setSavingRights(false); }
  };

  const openEdit = () => {
    setEditForm({ name: data.agent.name, mobile: data.agent.mobile, password: '' });
    setShowEdit(true);
  };

  const handleSaveDetails = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      const { password, ...rest } = editForm;
      await userAPI.update(id, password ? { ...rest, password } : rest);
      toast.success('Agent details updated');
      setShowEdit(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to update details'); }
    finally { setEditSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Deactivate agent ${data?.agent?.name}?`)) return;
    try {
      await userAPI.delete(id);
      toast.success('Agent deactivated');
      navigate('/agent-collection');
    } catch { toast.error('Failed to deactivate'); }
  };

  const toggleArea = (area) => {
    setSelAreas(prev =>
      prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]
    );
  };

  // Only the dates in the selected month the agent actually collected on, most recent first.
  const dailyMap = Object.fromEntries((data?.dailyTransactions || []).map(r => [r._id, r]));
  const collectedDays = Object.keys(dailyMap)
    .filter(day => day.startsWith(month))
    .sort((a, b) => b.localeCompare(a));

  if (loading) return <div style={{ textAlign: 'center', padding: 64 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
  if (!data) return null;

  const { agent, stats } = data;

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-5" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/agent-collection')}>
            <ArrowLeft size={14} />
          </button>
          <div>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700 }}>{agent.name}</h2>
            <p className="text-muted text-small">{agent.mobile} · <span style={{ textTransform: 'capitalize' }}>{agent.role}</span></p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={openEdit}>
            <Edit2 size={14} /> Edit Details
          </button>
          <button className="btn btn-danger" onClick={handleDelete}>
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>

      {showEdit && (
        <div className="modal-overlay" onClick={() => setShowEdit(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Agent Details</h3>
              <button className="modal-close" onClick={() => setShowEdit(false)}>✕</button>
            </div>
            <form onSubmit={handleSaveDetails}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input className="form-control" value={editForm.name}
                    onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Mobile *</label>
                  <input className="form-control" value={editForm.mobile}
                    onChange={e => setEditForm(f => ({ ...f, mobile: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label className="form-label">New Password (leave blank to keep)</label>
                  <input type="password" className="form-control" value={editForm.password}
                    onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))}
                    minLength={6} placeholder="Min 6 characters" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEdit(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={editSaving}>{editSaving ? 'Saving...' : 'Save Details'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Stat Cards ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard label={`Total collection (${format(new Date(month + '-01'), 'MMM yyyy')})`} total={stats.monthly.total} customers={stats.monthly.customers} color="var(--primary)" bg="var(--cb-eff6ff)" border="var(--cb-bfdbfe)" />
        <StatCard label="Todays Collection"        total={stats.todays.total}         customers={stats.todays.customers}         color="var(--c-c2410c)" bg="var(--cb-fff7ed)" border="var(--cb-fed7aa)" />
        <StatCard label="Total Pending Amount"     total={stats.pending.total}        customers={stats.pending.customers}        color="var(--c-b91c1c)" bg="var(--cb-fff1f2)" border="var(--cb-fecdd3)" />
      </div>

      {/* ── Monthly Total Transactions ── */}
      <div className="card mb-5">
        <div className="card-header" style={{ flexWrap: 'wrap', gap: 10 }}>
          <h3>Monthly Total Transactions</h3>
          <input
            type="month"
            className="form-control"
            value={month}
            max={format(new Date(), 'yyyy-MM')}
            onChange={e => setMonth(e.target.value)}
            style={{ width: 160 }}
          />
        </div>
        <div className="table-container" style={{ maxHeight: 360, overflowY: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>SNo</th>
                <th>Date</th>
                <th>Total Amount Collected</th>
                <th>Net Transaction Value</th>
              </tr>
            </thead>
            <tbody>
              {collectedDays.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: '.85rem' }}>
                    No collections for this month
                  </td>
                </tr>
              ) : collectedDays.map((day, i) => {
                const row = dailyMap[day];
                return (
                  <tr key={day}>
                    <td style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{i + 1}</td>
                    <td style={{ fontSize: '.83rem' }}>{format(new Date(day + 'T00:00:00'), 'dd-MMM-yyyy')}</td>
                    <td style={{ fontWeight: 700, color: 'var(--c-15803d)', fontSize: '.85rem' }}>
                      ₹{fmt(row.total)}
                    </td>
                    <td style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '.85rem' }}>
                      ₹{fmt(row.total)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Agent Area + Agent Permission (side by side) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* Agent Area */}
        <div className="card">
          <div className="card-header"><h3>Agent Area</h3></div>
          <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search area..."
              value={areaSearch}
              onChange={e => setAreaSearch(e.target.value)}
              style={{ marginBottom: 10 }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem', fontWeight: 600 }}>
              <input type="checkbox"
                checked={selAreas.length === areas.length && areas.length > 0}
                onChange={() => setSelAreas(selAreas.length === areas.length ? [] : [...areas])}
              />
              Add All Area
            </label>
          </div>
          <div style={{ maxHeight: 480, overflowY: 'auto', padding: '8px 0' }}>
            {areas.length === 0 ? (
              <div style={{ padding: '24px 20px', color: 'var(--text-muted)', fontSize: '.85rem' }}>
                No areas found. Add customer areas first.
              </div>
            ) : areas.filter(a => a.toLowerCase().includes(areaSearch.toLowerCase())).length === 0 ? (
              <div style={{ padding: '24px 20px', color: 'var(--text-muted)', fontSize: '.85rem' }}>
                No areas match "{areaSearch}"
              </div>
            ) : (
              areas.filter(a => a.toLowerCase().includes(areaSearch.toLowerCase())).map(area => (
                <label key={area} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 20px', cursor: 'pointer', fontSize: '.85rem', borderBottom: '1px solid var(--border)', transition: 'background .1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <input type="checkbox"
                    checked={selAreas.includes(area)}
                    onChange={() => toggleArea(area)}
                    style={{ accentColor: 'var(--c-f97316)', width: 15, height: 15 }}
                  />
                  {area}
                </label>
              ))
            )}
          </div>
          <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', textAlign: 'center' }}>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleSaveAreas} disabled={saving}>
              Update ✓
            </button>
          </div>
        </div>

        {/* Agent Permission */}
        <div className="card">
          <div className="card-header"><h3>Agent Permission</h3></div>
          <div style={{ padding: '8px 20px' }}>
            {fieldModules.map(mod => {
              const action = mod.actions[0].key;
              return (
                <RightCheckbox key={mod.key}
                  checked={!!fieldRights[mod.key]?.[action]}
                  onChange={() => toggleFieldRight(mod.key, action)}
                  label={mod.label.replace(' (Field)', '')}
                />
              );
            })}
          </div>
          <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', textAlign: 'center' }}>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleSaveFieldRights} disabled={savingRights}>
              Update ✓
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
